import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import {
  getProfileInvoicesRequest,
  getInvoiceRequest,
  getInvoiceDetailsRequest,
  resolveInvoiceDetailsLinks
} from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type {
  InvoicesResponse,
  Invoice,
  InvoiceDetailsResponse
} from "Api/billingSystem/types";
import type { RefOrganization } from "Api/ref/Organization.types";
import type { RefProject } from "Api/ref/Project.types";

// Thunks
export const getProfileInvoices = createAppAsyncThunk(
  "billingSystem/invoices/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getProfileInvoicesRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getInvoice = createAppAsyncThunk(
  "billingSystem/invoices/getOne",
  async (
    {
      billingProfileId,
      invoiceId
    }: {
      billingProfileId: string;
      invoiceId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await getInvoiceRequest(billingProfileId, invoiceId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getInvoiceDetails = createAppAsyncThunk(
  "billingSystem/invoices/getDetails",
  async (
    {
      billingProfileId,
      invoiceId
    }: {
      billingProfileId: string;
      invoiceId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const details = await getInvoiceDetailsRequest(
        billingProfileId,
        invoiceId
      );
      const api = resolveInvoiceDetailsLinks(details);
      // Best-effort: a failing ref endpoint shouldn't hide the invoice — the
      // tables fall back to organization/project IDs when ref data is missing.
      // Both pages are resolved concurrently, each isolated so one failing
      // doesn't drop the other.
      const [organizations, projects] = await Promise.all([
        api.organizations?.().catch(err => {
          logger(err);
          return undefined;
        }),
        api.projects?.().catch(err => {
          logger(err);
          return undefined;
        })
      ]);
      return { details, organizations, projects };
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type InvoicesState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: InvoicesResponse;
      error?: string;
    }
  >;
  byId: Record<
    string,
    {
      loading: boolean;
      data?: Invoice;
      error?: string;
    }
  >;
  detailsByInvoiceId: Record<
    string,
    {
      loading: boolean;
      data?: InvoiceDetailsResponse;
      // Ref data keyed by id, resolved from the invoice details `ref:` links so
      // the page can display organization and project names instead of IDs.
      organizations?: Record<string, RefOrganization>;
      projects?: Record<string, RefProject>;
      error?: string;
    }
  >;
};

const initialState: InvoicesState = {
  byProfileId: {},
  byId: {},
  detailsByInvoiceId: {}
};

// Slice
const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Get invoices by profile
    builder
      .addCase(getProfileInvoices.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getProfileInvoices.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getProfileInvoices.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });

    // Get single invoice
    builder
      .addCase(getInvoice.pending, (state, { meta }) => {
        setDeep(state, ["byId", meta.arg.invoiceId, "loading"], true);
        setDeep(state, ["byId", meta.arg.invoiceId, "error"], undefined);
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        const { invoiceId } = action.meta.arg;
        setDeep(state, ["byId", invoiceId, "loading"], false);
        setDeep(state, ["byId", invoiceId, "data"], action.payload);
      })
      .addCase(getInvoice.rejected, (state, action) => {
        const { invoiceId } = action.meta.arg;
        setDeep(state, ["byId", invoiceId, "loading"], false);
        setDeep(state, ["byId", invoiceId, "error"], action.payload?.error);
      });

    // Get invoice details
    builder
      .addCase(getInvoiceDetails.pending, (state, { meta }) => {
        setDeep(
          state,
          ["detailsByInvoiceId", meta.arg.invoiceId, "loading"],
          true
        );
        setDeep(
          state,
          ["detailsByInvoiceId", meta.arg.invoiceId, "error"],
          undefined
        );
      })
      .addCase(getInvoiceDetails.fulfilled, (state, action) => {
        const { invoiceId } = action.meta.arg;
        const { details, organizations, projects } = action.payload;
        setDeep(state, ["detailsByInvoiceId", invoiceId, "loading"], false);
        setDeep(state, ["detailsByInvoiceId", invoiceId, "data"], details);
        setDeep(
          state,
          ["detailsByInvoiceId", invoiceId, "organizations"],
          organizations
        );
        setDeep(state, ["detailsByInvoiceId", invoiceId, "projects"], projects);
      })
      .addCase(getInvoiceDetails.rejected, (state, action) => {
        const { invoiceId } = action.meta.arg;
        setDeep(state, ["detailsByInvoiceId", invoiceId, "loading"], false);
        setDeep(
          state,
          ["detailsByInvoiceId", invoiceId, "error"],
          action.payload?.error
        );
      });
  }
});

export default invoicesSlice.reducer;

// Selectors
const invoicesStateSelector = (state: RootState) => state.invoices;

// Invoices by profile ID
export const selectProfileInvoices = createSelector(
  invoicesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.data?.items
);

export const selectProfileInvoicesLoading = createSelector(
  invoicesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileInvoicesError = createSelector(
  invoicesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

// Single invoice by ID
export const selectInvoiceById = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.byId[invoiceId]?.data
);

export const selectInvoiceLoadingById = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.byId[invoiceId]?.loading ?? false
);

export const selectInvoiceErrorById = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.byId[invoiceId]?.error
);

// Invoice details
export const selectInvoiceDetails = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.detailsByInvoiceId[invoiceId]?.data?.items
);

export const selectInvoiceDetailsLoading = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.detailsByInvoiceId[invoiceId]?.loading ?? false
);

export const selectInvoiceDetailsError = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.detailsByInvoiceId[invoiceId]?.error
);

// Ref data resolved alongside the invoice details, keyed by organization /
// project id, used to display names instead of IDs.
export const selectInvoiceOrganizations = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.detailsByInvoiceId[invoiceId]?.organizations
);

export const selectInvoiceProjects = createSelector(
  invoicesStateSelector,
  (_: RootState, invoiceId: string) => invoiceId,
  (state, invoiceId) => state.detailsByInvoiceId[invoiceId]?.projects
);
